import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, reportClientError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared";
import { PAGE_HELP } from "@/components/help";
import { useAuth } from "@/lib/auth";
import { ArrowLeftRight, Check, CreditCard, Crown, FlaskConical, Loader2, RotateCcw, Settings2, Shield, Sparkles, Trash2, XCircle, Zap } from "lucide-react";

const UsageBar = ({ label, used, limit, testId }) => {
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="text-muted-foreground text-xs">{used} / {unlimited ? "Unlimited" : limit}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${pct >= 90 ? "bg-[hsl(var(--destructive))]" : pct >= 70 ? "bg-[hsl(var(--warning))]" : "bg-primary"}`} style={{ width: unlimited ? "6%" : `${pct}%` }} />
      </div>
    </div>
  );
};

const TIERS = [
  {
    plan: "basic",
    packageId: "basic_monthly",
    name: "Basic",
    icon: Shield,
    tagline: "Everything one pet needs, every day",
    features: [
      "1 pet profile with full history",
      "Daily check-ins, streaks & wellness logs",
      "A fresh personalized ritual + enrichment plan every day",
      "Full Bond Score with trends & history",
      "AI Bond Coach for everyday questions",
      "Vet-ready reports for appointments",
    ],
  },
  {
    plan: "pro",
    packageId: "pro_monthly",
    name: "Pro",
    icon: Zap,
    tagline: "For multi-pet homes & care teams",
    highlight: true,
    features: [
      "Everything in Basic — for up to 3 pets",
      "Care Circle: share access with sitters, family & your vet",
      "Voice Support agent",
      "AI Specialists team (behavior, nutrition, training...)",
      "3x the AI Coach conversations",
      "Priority ticket handling",
    ],
  },
  {
    plan: "premium",
    packageId: "premium_monthly",
    name: "Premium",
    icon: Crown,
    tagline: "The full wellness OS, no limits",
    features: [
      "Everything in Pro — with unlimited pets",
      "Unlimited vet-ready reports",
      "Full Agent Command Center, unlimited runs",
      "Deepest AI allowance for heavy daily use",
      "Priority human support",
      "First access to new features & wearables",
    ],
  },
];

export default function Billing() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busyPkg, setBusyPkg] = useState(null);
  // payment return state: null | "checking" | "paid" | "expired" | "timeout" | "canceled"
  const [payState, setPayState] = useState(null);
  const [paidPlan, setPaidPlan] = useState(null);
  const [switchTarget, setSwitchTarget] = useState(null); // tier object pending switch confirmation
  const [startBeta, setStartBeta] = useState(false);
  const [startTrial, setStartTrial] = useState(false); // new signup: must pick a plan + card for the free trial
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [actBusy, setActBusy] = useState(false);

  const cancelMembership = async () => {
    setActBusy(true);
    try {
      const { data: r } = await api.post("/billing/cancel");
      toast.success(`Cancellation scheduled — you keep access until ${r.membershipEndsAt ? new Date(r.membershipEndsAt).toLocaleDateString() : "the end of your billing period"}.`);
      setCancelOpen(false);
      await load();
      await refreshAuthUser();
    } catch (e) {
      reportClientError("billing.cancel_membership", e);
      toast.error(errMsg(e));
    } finally { setActBusy(false); }
  };

  const startFree = async () => {
    setBusyPkg("free");
    try {
      const { data: r } = await api.post("/billing/start-free");
      toast.success(`Your free month has started — ${r.user?.name ? "welcome" : "welcome"}! Add your pet and do your first check-in.`);
      await refreshAuthUser(); // lifts the subscription gate immediately
      navigate("/dashboard");
    } catch (e) {
      reportClientError("billing.start_free", e);
      toast.error(errMsg(e));
      setBusyPkg(null);
    }
  };

  const resumeMembership = async () => {
    setActBusy(true);
    try {
      await api.post("/billing/resume");
      toast.success("Welcome back — your membership will keep renewing as usual.");
      await load();
      await refreshAuthUser();
    } catch (e) {
      reportClientError("billing.resume_membership", e);
      toast.error(errMsg(e));
    } finally { setActBusy(false); }
  };

  const deleteAccount = async () => {
    setActBusy(true);
    try {
      await api.delete("/account", { data: { password: deletePassword } });
      toast.success("Your account has been deleted. Take good care — you're always welcome back.");
      setDeleteOpen(false);
      await logout();
      navigate("/");
    } catch (e) {
      reportClientError("account.self_delete", e);
      toast.error(errMsg(e));
    } finally { setActBusy(false); }
  };
  const pollRef = useRef(0);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/billing/plan"); setData(d); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshAuthUser = useCallback(async () => {
    try { const { data: me } = await api.get("/auth/me"); setUser(me); } catch (e) { console.warn("Auth refresh failed (non-blocking):", e?.message); }
  }, [setUser]);

  // Handle return from Stripe: poll checkout status (this is what finalizes the upgrade)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("startBeta")) {
      setStartBeta(true);
      window.history.replaceState({}, "", "/billing");
      return;
    }
    if (params.get("startTrial")) {
      setStartTrial(true);
      window.history.replaceState({}, "", "/billing");
      return;
    }
    if (params.get("canceled")) {
      setPayState("canceled");
      toast.info("Checkout canceled — no charge was made.");
      window.history.replaceState({}, "", "/billing");
      return;
    }
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPayState("checking");
    const poll = async (attempt) => {
      if (attempt >= 8) { setPayState("timeout"); return; }
      try {
        const { data: s } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (s.paymentStatus === "paid") {
          setPayState("paid");
          setPaidPlan(s.plan || null);
          toast.success(`Payment successful — welcome to ${(s.plan || "your new plan").replace(/^./, (c) => c.toUpperCase())}!`);
          window.history.replaceState({}, "", "/billing");
          load();
          refreshAuthUser(); // lifts the subscription gate immediately
          return;
        }
        if (s.status === "expired") { setPayState("expired"); return; }
        pollRef.current = setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setPayState("timeout");
        toast.error(errMsg(e));
      }
    };
    poll(0);
    return () => clearTimeout(pollRef.current);
  }, [load, refreshAuthUser]);

  const buy = async (packageId) => {
    setBusyPkg(packageId);
    try {
      const { data: r } = await api.post("/billing/checkout", {
        packageId,
        originUrl: window.location.origin,
      });
      if (r.url) window.location.href = r.url; // redirect to Stripe Checkout
      else throw new Error("No checkout URL received.");
    } catch (e) {
      toast.error(errMsg(e));
      setBusyPkg(null);
    }
  };

  const openPortal = async () => {
    setBusyPkg("portal");
    try {
      const { data: r } = await api.post("/billing/portal", { originUrl: window.location.origin });
      if (r.url) window.location.href = r.url; // redirect to Stripe Billing Portal
      else throw new Error("No portal URL received.");
    } catch (e) {
      reportClientError("billing.portal", e);
      toast.error(errMsg(e));
      setBusyPkg(null);
    }
  };

  const confirmSwitch = async () => {
    if (!switchTarget) return;
    setBusyPkg(switchTarget.packageId);
    try {
      const { data: r } = await api.post("/billing/change-plan", { packageId: switchTarget.packageId });
      toast.success(`Plan changed to ${r.plan.replace(/^./, (c) => c.toUpperCase())} — your next invoice is prorated automatically.`);
      setSwitchTarget(null);
      await load();
    } catch (e) {
      reportClientError("billing.change_plan", e, { packageId: switchTarget?.packageId });
      toast.error(errMsg(e));
    } finally {
      setBusyPkg(null);
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading plan...</p>;
  const currentPlan = data.plan;
  const isPremium = currentPlan === "premium";

  return (
    <div className="max-w-5xl">
      <PageHeader title="Plan & Billing" subtitle="Monthly auto-renewing subscription with secure Stripe checkout." help={PAGE_HELP.billing}>
        <div className="flex items-center gap-2">
          {data.hasSubscription && (
            <Button size="sm" variant="outline" className="rounded-lg" onClick={openPortal} disabled={busyPkg === "portal"} data-testid="billing-manage-subscription-button">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> {busyPkg === "portal" ? "Opening..." : "Manage subscription"}
            </Button>
          )}
          <Badge variant="secondary" className={`border-0 capitalize ${isPremium ? "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]" : ""}`} data-testid="billing-plan-badge">
            {isPremium && <Crown className="w-3 h-3 mr-1" />} {currentPlan} plan
          </Badge>
        </div>
      </PageHeader>

      {(startBeta || (startTrial && data.betaTester)) && !data.hasSubscription && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--warning)/0.5)] bg-[hsl(var(--warning)/0.06)]" data-testid="beta-start-card">
          <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <FlaskConical className="w-8 h-8 text-[hsl(var(--warning))] shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Start your {data.trialDays}-day Premium beta trial</p>
              <p className="text-xs text-muted-foreground mt-0.5">Card required now, first charge only after {data.trialDays} days — with your lifetime 10% beta discount already applied. Cancel anytime.</p>
            </div>
            <Button className="rounded-xl" onClick={() => buy("premium_monthly")} disabled={!!busyPkg} data-testid="beta-start-trial-button">
              <CreditCard className="w-4 h-4 mr-2" /> {busyPkg === "premium_monthly" ? "Opening Stripe..." : "Start Premium trial"}
            </Button>
          </CardContent>
        </Card>
      )}
      {startTrial && !data.betaTester && !data.hasSubscription && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--accent)/0.18)]" data-testid="trial-start-card">
          <CardContent className="p-5 flex items-start gap-4">
            <CreditCard className="w-8 h-8 text-primary shrink-0" />
            <div>
              <p className="text-sm font-semibold">One last step — pick how you'd like to start</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {data.freeTierAvailable
                  ? `Try Soula Paws free for ${data.freeTierDays} days with 1 pet — no card needed. Or pick a paid plan${data.trialDays > 0 ? ` with a ${data.trialDays}-day free trial (card collected by Stripe, first charge after the trial)` : ""}. Cancel anytime.`
                  : "Your card is collected securely by Stripe now, and your first charge only happens after the trial ends. Cancel anytime — no charge if you cancel during the trial."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {data.freeTierExpired && !data.hasSubscription && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--warning)/0.5)] bg-[hsl(var(--warning)/0.06)]" data-testid="free-expired-card">
          <CardContent className="p-5 flex items-start gap-4">
            <Sparkles className="w-8 h-8 text-[hsl(var(--warning))] shrink-0" />
            <div>
              <p className="text-sm font-semibold">Your free month has ended — pick a plan to continue</p>
              <p className="text-xs text-muted-foreground mt-0.5">All of {""}your pet's data, history, and Bond Score are safe and waiting. Choose a plan below to jump right back in.</p>
            </div>
          </CardContent>
        </Card>
      )}
      {data.betaTester && !startBeta && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--warning)/0.4)]" data-testid="beta-tester-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <FlaskConical className="w-5 h-5 text-[hsl(var(--warning))]" />
            <p className="text-sm">Beta tester: your <strong>10% lifetime discount</strong> is applied automatically at checkout.</p>
          </CardContent>
        </Card>
      )}

      {payState === "checking" && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--info)/0.4)]" data-testid="payment-checking-card">
          <CardContent className="p-5 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--info))]" />
            <p className="text-sm">Confirming your payment with Stripe... this takes a few seconds.</p>
          </CardContent>
        </Card>
      )}
      {payState === "paid" && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.06)]" data-testid="payment-success-card">
          <CardContent className="p-5 flex items-center gap-3">
            <Crown className="w-5 h-5 text-[hsl(var(--success))]" />
            <div>
              <p className="text-sm font-semibold">Payment successful — you're on {(paidPlan || "your new plan").replace(/^./, (c) => c.toUpperCase())} now.</p>
              <p className="text-xs text-muted-foreground mt-0.5">Your new limits are active immediately — check the usage meters below.</p>
            </div>
          </CardContent>
        </Card>
      )}
      {(payState === "expired" || payState === "timeout") && (
        <Card className="rounded-2xl mb-6 border-[hsl(var(--warning)/0.4)]" data-testid="payment-problem-card">
          <CardContent className="p-5">
            <p className="text-sm font-medium">{payState === "expired" ? "That checkout session expired." : "We couldn't confirm the payment yet."}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{payState === "expired" ? "No charge was made — you can start a new checkout below." : "If you completed payment, it will be applied automatically within a minute (our Stripe webhook also confirms it). You can refresh this page to check."}</p>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl mb-6" data-testid="billing-usage-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Usage this month</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {data.bonusAiCredits > 0 && (
            <Badge variant="secondary" className="border-0 bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]" data-testid="bonus-credits-badge">
              🎁 {data.bonusAiCredits} bonus AI credits available (used after your monthly allowance)
            </Badge>
          )}
          <UsageBar label="AI calls / credits (coach, rituals, voice)" used={data.usage.aiCallsThisMonth} limit={data.limits.aiCallsPerMonth} testId="usage-ai" />
          <UsageBar label="Reports generated" used={data.usage.reportsThisMonth} limit={data.limits.reportsPerMonth} testId="usage-reports" />
          <UsageBar label="Agent runs" used={data.usage.agentRunsThisMonth} limit={data.limits.agentRunsPerMonth} testId="usage-agents" />
          <UsageBar label="Active pet profiles" used={data.usage.activePets} limit={data.limits.maxPets} testId="usage-pets" />
        </CardContent>
      </Card>

      {(() => {
        const showFreeCard = (data.freeTierAvailable && !data.hasSubscription) || currentPlan === "free";
        const freeActive = currentPlan === "free" && !data.freeTierExpired && !user?.needsSubscription;
        const freeDaysLeft = data.freeUntil ? Math.max(0, Math.ceil((new Date(data.freeUntil).getTime() - Date.now()) / 86400000)) : null;
        return (
          <div className={`grid sm:grid-cols-2 ${showFreeCard ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
            {showFreeCard && (
              <Card className={`rounded-2xl flex flex-col ${freeActive ? "border-primary" : "border-dashed"}`} data-testid="billing-free-card">
                <CardContent className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> Free</p>
                    <Badge variant="secondary" className="border-0 bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] text-[10px]">No card</Badge>
                  </div>
                  <p className="text-2xl font-bold mt-2">$0<span className="text-sm text-muted-foreground font-normal"> / {data.freeTierDays} days</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">Test the product with one pet</p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {["1 pet profile", "Daily check-ins & Bond Score", "Personalized daily ritual + enrichment ideas", "Starter AI Coach (10 conversations)", `Full ${data.freeTierDays} days — then pick a plan`].map((f) => (
                      <li key={f} className="text-sm flex gap-2"><Check className="w-4 h-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                  {freeActive ? (
                    <Badge variant="secondary" className="border-0 mt-4 self-start" data-testid="billing-free-active-badge">
                      Active{freeDaysLeft !== null ? ` · ${freeDaysLeft} day${freeDaysLeft === 1 ? "" : "s"} left` : ""}
                    </Badge>
                  ) : currentPlan === "free" ? (
                    <Badge variant="secondary" className="border-0 mt-4 self-start bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]" data-testid="billing-free-ended-badge">Free month ended</Badge>
                  ) : (
                    <Button className="w-full rounded-xl mt-4" variant="outline" onClick={startFree} disabled={!!busyPkg || payState === "checking"} data-testid="billing-start-free-button">
                      <Sparkles className="w-4 h-4 mr-2" /> {busyPkg === "free" ? "Starting..." : "Start free — no card"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
            {TIERS.map((tier) => {
          // A plan only counts as "current" once the user actually has entitlement
          // (subscription, trial, or admin comp) - new signups must pick a plan + card first.
          const isCurrent = currentPlan === tier.plan && !user?.needsSubscription;
          const pkg = data.packages?.[tier.packageId];
          const price = pkg?.amount ?? 0;
          const Icon = tier.icon;
          return (
            <Card
              key={tier.plan}
              className={`rounded-2xl flex flex-col ${isCurrent ? "border-primary" : tier.highlight ? "border-[hsl(var(--warning)/0.5)] shadow-[0_10px_30px_-20px_rgba(0,0,0,0.3)]" : ""}`}
              data-testid={`billing-${tier.plan}-card`}
            >
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold flex items-center gap-1.5">
                    <Icon className={`w-4 h-4 ${tier.plan === "premium" ? "text-[hsl(var(--warning))]" : "text-primary"}`} /> {tier.name}
                  </p>
                  {tier.highlight && !isCurrent && <Badge variant="secondary" className="border-0 bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] text-[10px]">Popular</Badge>}
                </div>
                <p className="text-2xl font-bold mt-2">${price}<span className="text-sm text-muted-foreground font-normal"> / month</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">{tier.tagline}</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {tier.features.map((f) => <li key={f} className="text-sm flex gap-2"><Check className="w-4 h-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />{f}</li>)}
                </ul>
                {isCurrent ? (
                  <Badge variant="secondary" className="border-0 mt-4 self-start" data-testid={`billing-current-plan-badge-${tier.plan}`}>Current plan</Badge>
                ) : data.hasSubscription ? (
                  <Button
                    className="w-full rounded-xl mt-4"
                    variant="outline"
                    onClick={() => setSwitchTarget(tier)}
                    disabled={!!busyPkg || payState === "checking"}
                    data-testid={`billing-switch-button-${tier.plan}`}
                  >
                    <ArrowLeftRight className="w-4 h-4 mr-2" /> {busyPkg === tier.packageId ? "Switching..." : `Switch to ${tier.name}`}
                  </Button>
                ) : (
                  <div className="mt-4">
                    <Button
                      className="w-full rounded-xl"
                      variant={tier.highlight ? "default" : "outline"}
                      onClick={() => buy(tier.packageId)}
                      disabled={!!busyPkg || payState === "checking"}
                      data-testid={`billing-upgrade-button-${tier.plan}`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" /> {busyPkg === tier.packageId ? "Opening Stripe..." : data.trialDays > 0 ? `Start ${data.trialDays}-day free trial` : `Get ${tier.name}`}
                    </Button>
                    {data.trialDays > 0 && <p className="text-[10px] text-muted-foreground text-center mt-1.5">Card required — first charge after the trial. Cancel anytime.</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
          </div>
        );
      })()}

      <p className="text-[11px] text-muted-foreground mt-4" data-testid="billing-note">
        Subscriptions renew monthly and are processed securely by Stripe — card details never touch Soula Paws servers. Cancel or change plans anytime via Manage subscription.
      </p>

      {/* Membership & Account management - available to every user */}
      <div className="mt-10">
        <h2 className="text-base font-semibold mb-3">Membership & account</h2>
        <Card className="rounded-2xl" data-testid="membership-settings-card">
          <CardContent className="p-5 space-y-5">
            {/* Cancel / resume membership */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium">Cancel membership</p>
                {data.cancelAtPeriodEnd ? (
                  <p className="text-xs text-[hsl(var(--warning))] mt-0.5" data-testid="membership-ending-notice">
                    Cancellation scheduled — your plan stays active until {data.membershipEndsAt ? new Date(data.membershipEndsAt).toLocaleDateString() : "the end of this billing period"}, then won't renew.
                  </p>
                ) : data.hasSubscription ? (
                  <p className="text-xs text-muted-foreground mt-0.5">Stops future renewals. You keep full access until the end of your current billing period.</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">No active paid membership — trial or complimentary access simply expires on its own.</p>
                )}
              </div>
              {data.cancelAtPeriodEnd ? (
                <Button variant="outline" className="rounded-xl shrink-0" onClick={resumeMembership} disabled={actBusy} data-testid="resume-membership-button">
                  <RotateCcw className="w-4 h-4 mr-2" /> {actBusy ? "Resuming..." : "Resume membership"}
                </Button>
              ) : (
                <Button variant="outline" className="rounded-xl shrink-0 text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.35)] hover:bg-[hsl(var(--destructive)/0.06)] hover:text-[hsl(var(--destructive))]" onClick={() => setCancelOpen(true)} disabled={!data.hasSubscription || actBusy} data-testid="cancel-membership-button">
                  <XCircle className="w-4 h-4 mr-2" /> Cancel membership
                </Button>
              )}
            </div>

            <div className="border-t border-border pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium">Delete account</p>
                <p className="text-xs text-muted-foreground mt-0.5">Permanently removes your profile, pets, check-ins, photos, and all data — and stops billing immediately. This can't be undone.</p>
              </div>
              <Button variant="outline" className="rounded-xl shrink-0 text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.35)] hover:bg-[hsl(var(--destructive)/0.06)] hover:text-[hsl(var(--destructive))]" onClick={() => { setDeletePassword(""); setDeleteOpen(true); }} data-testid="delete-account-button">
                <Trash2 className="w-4 h-4 mr-2" /> Delete my account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cancel membership confirm */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-membership-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your membership?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll keep full access until the end of your current billing period, then your plan simply won't renew. You can resume anytime before then — your pet's data and history stay safe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-membership-keep">Keep my membership</AlertDialogCancel>
            <AlertDialogAction onClick={cancelMembership} className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.9)]" data-testid="cancel-membership-confirm">
              {actBusy ? "Cancelling..." : "Yes, cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete account confirm (password required) */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account forever?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes your profile, pets, check-ins, rituals, photos, and reports, and cancels any active subscription immediately. Enter your password to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder="Your account password" data-testid="delete-account-password-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">Keep my account</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAccount} disabled={!deletePassword || actBusy} className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.9)]" data-testid="delete-account-confirm">
              {actBusy ? "Deleting..." : "Delete everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!switchTarget} onOpenChange={(open) => !open && setSwitchTarget(null)}>
        <AlertDialogContent data-testid="switch-plan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to {switchTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription changes immediately and Stripe prorates the difference fairly on your next invoice — you only pay for what you use. No new checkout needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="switch-plan-cancel">Keep my plan</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSwitch} data-testid="switch-plan-confirm">Switch plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

